import React from 'react';
import {
  Smartphone,
  Bot,
  Code2,
  Rocket,
  Users
} from 'lucide-react';
import { Stat, Service, PortfolioHighlight, Benefit, ContactInfo, NavItem } from '../types';

export const CONTACT_INFO: ContactInfo = {
  email: 'aaron.jags@gmail.com',
  linkedin: 'https://www.linkedin.com/in/aaron-garcia-69a18681/',
  portfolioUrl: 'https://drive.google.com/file/d/1444M3G5BRXkrFkjZMMCysGIC6zvAr3ae/view?usp=sharing'
};

export interface NavItemWithKey extends NavItem {
  i18nKey: string;
}

export const NAV_ITEMS: NavItemWithKey[] = [
  { label: 'Experiencia', href: '#experiencia', i18nKey: 'nav.experience' },
  { label: 'Consultoría', href: '/consultoria', i18nKey: 'nav.consulting' },
  { label: 'Contactar', href: '#contacto', isPrimary: true, i18nKey: 'nav.contact' }
];

export interface StatWithKey extends Stat {
  i18nLabel: string;
}

export const STATS: StatWithKey[] = [
  {
    label: "Años de Experiencia",
    value: "10+",
    icon: <Code2 className="w-5 h-5 text-blue-400" />,
    i18nLabel: "stats.years"
  },
  {
    label: "Descargas en Google Play",
    value: "100K+",
    icon: <Smartphone className="w-5 h-5 text-purple-400" />,
    i18nLabel: "stats.products"
  },
  {
    label: "Usuarios alcanzados",
    value: "1M+",
    icon: <Users className="w-5 h-5 text-emerald-400" />,
    i18nLabel: "stats.users"
  },
];

export interface ServiceWithKey extends Service {
  i18nTitle: string;
  i18nDesc: string;
}

export const SERVICES: ServiceWithKey[] = [
  {
    title: "Arquitectura Móvil",
    description: "Android nativo (Kotlin, Jetpack Compose), Flutter y React Native, con Swift para funcionalidades nativas y bridges.",
    icon: <Smartphone className="w-10 h-10 text-blue-600" />,
    tags: ["Kotlin/Compose", "Flutter", "React Native"],
    i18nTitle: "services.items.mobile.title",
    i18nDesc: "services.items.mobile.desc"
  },
  {
    title: "Software con IA",
    description: "Lo que decide cuándo puede salir a producción el código que escribe la IA.",
    icon: <Bot className="w-10 h-10 text-purple-600" />,
    tags: ["Claude Code", "MCP", "Evals"],
    i18nTitle: "services.items.ai.title",
    i18nDesc: "services.items.ai.desc"
  },
  {
    title: "Full-Stack & Backend",
    description: "Lo que hay detrás de la app: Python y FastAPI, TypeScript y Node, Supabase y Postgres.",
    icon: <Rocket className="w-10 h-10 text-orange-600" />,
    tags: ["FastAPI", "Node.js", "Supabase"],
    i18nTitle: "services.items.web.title",
    i18nDesc: "services.items.web.desc"
  }
];

export interface PortfolioHighlightWithKey extends PortfolioHighlight {
  i18nRole: string;
  i18nDate: string;
  i18nDesc: string;
}

export const PORTFOLIO_HIGHLIGHTS: PortfolioHighlightWithKey[] = [
  {
    company: "Agave Matchmaker",
    role: "Android Engineer, Staff since Nov 2025",
    date: "Since Nov 2020 · part-time",
    desc: "Android app with 100K+ downloads, formerly Tequila Matchmaker.",
    color: "bg-emerald-600",
    i18nRole: "experience.portfolio_highlights.agave.role",
    i18nDate: "experience.portfolio_highlights.agave.date",
    i18nDesc: "experience.portfolio_highlights.agave.desc"
  },
  {
    company: "Eclia",
    role: "CTO (part-time)",
    date: "Since Mar 2026",
    desc: "Founder-led startup for church communities.",
    color: "bg-rose-600",
    i18nRole: "experience.portfolio_highlights.eclia.role",
    i18nDate: "experience.portfolio_highlights.eclia.date",
    i18nDesc: "experience.portfolio_highlights.eclia.desc"
  },
  {
    company: "ATASA",
    role: "Full-Stack Mobile Engineer (freelance)",
    date: "Oct 2025 – Oct 2026 · part-time",
    desc: "Airport taxi service: passenger and driver apps.",
    color: "bg-teal-600",
    i18nRole: "experience.portfolio_highlights.atasa.role",
    i18nDate: "experience.portfolio_highlights.atasa.date",
    i18nDesc: "experience.portfolio_highlights.atasa.desc"
  },
  {
    company: "Panasonic Well",
    role: "Senior Full Stack Software Engineer",
    date: "Sep 2025 – Nov 2025",
    desc: "Contract through Taller.",
    color: "bg-blue-600",
    i18nRole: "experience.portfolio_highlights.panasonic.role",
    i18nDate: "experience.portfolio_highlights.panasonic.date",
    i18nDesc: "experience.portfolio_highlights.panasonic.desc"
  },
  {
    company: "Couchsurfing",
    role: "Senior Software Engineer",
    date: "Feb 2025 – Aug 2025",
    desc: "Flutter app of a global consumer product.",
    color: "bg-orange-500",
    i18nRole: "experience.portfolio_highlights.couchsurfing.role",
    i18nDate: "experience.portfolio_highlights.couchsurfing.date",
    i18nDesc: "experience.portfolio_highlights.couchsurfing.desc"
  },
  {
    company: "Kamet",
    role: "Senior Mobile Engineer",
    date: "Feb 2024 – May 2025",
    desc: "Zero tests to a banking launch.",
    color: "bg-purple-600",
    i18nRole: "experience.portfolio_highlights.kamet.role",
    i18nDate: "experience.portfolio_highlights.kamet.date",
    i18nDesc: "experience.portfolio_highlights.kamet.desc"
  },
  {
    company: "Nubank",
    role: "Software Engineer",
    date: "Oct 2021 – Nov 2023",
    desc: "Customer acquisition for Mexico.",
    color: "bg-violet-700",
    i18nRole: "experience.portfolio_highlights.nubank.role",
    i18nDate: "experience.portfolio_highlights.nubank.date",
    i18nDesc: "experience.portfolio_highlights.nubank.desc"
  }
];

export interface BenefitWithKey extends Benefit {
  i18nTitle: string;
  i18nDesc: string;
}

export const BENEFITS: BenefitWithKey[] = [
  {
    title: "Impacto medible",
    description: "Una base de código sin tests llevada a un lanzamiento bancario, una app de conductores aprobada y publicada en App Store y pagos con dinero real liberados detrás de feature flags.",
    i18nTitle: "experience.benefits.business_vision.title",
    i18nDesc: "experience.benefits.business_vision.desc"
  },
  {
    title: "Calidad Global",
    description: "Estándares internacionales de desarrollo, seguridad y rendimiento.",
    i18nTitle: "experience.benefits.global_quality.title",
    i18nDesc: "experience.benefits.global_quality.desc"
  },
  {
    title: "Full Stack & AI",
    description: "Desde la base de datos hasta la interfaz visual, pasando por la IA.",
    i18nTitle: "experience.benefits.full_stack.title",
    i18nDesc: "experience.benefits.full_stack.desc"
  }
];

export const TRUSTED_COMPANIES = ["Nubank", "Couchsurfing", "Kamet", "Crabi", "ATASA", "Agave Matchmaker"];
